import { apiClient, authFetch, API_BASE_URL } from './apiClient';

const BASE = `${API_BASE_URL}/api/finance`;

const toQuery = (params = {}) => {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    query.set(key, String(value));
  });
  const suffix = query.toString();
  return suffix ? `?${suffix}` : '';
};

export const getBudgets = async (params = {}) => apiClient(`${BASE}/budgets${toQuery(params)}`);

export const getBudget = async (budgetId, params = {}) =>
  apiClient(`${BASE}/budgets/${budgetId}${toQuery(params)}`);

export const exportBudgetXlsx = async (budgetId, params = {}) => {
  const response = await authFetch(`${BASE}/budgets/${budgetId}/export${toQuery(params)}`, {
    timeoutMs: 60000,
  });
  if (!response.ok) {
    let detail = `Error ${response.status}: ${response.statusText}`;
    try {
      const data = await response.json();
      if (data?.detail) {
        detail = typeof data.detail === 'string' ? data.detail : JSON.stringify(data.detail);
      }
    } catch {
      /* ignore */
    }
    throw new Error(detail);
  }
  const blob = await response.blob();
  const disposition = String(response.headers.get('content-disposition') || '');
  const match = disposition.match(/filename="?([^"]+)"?/i);
  const filename = match?.[1] || 'presupuesto.xlsx';
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
  return { filename, size: blob.size };
};

export const createBudget = async (payload) =>
  apiClient(`${BASE}/budgets`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });

export const updateBudget = async (budgetId, payload) =>
  apiClient(`${BASE}/budgets/${budgetId}`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });

export const setBudgetStatus = async (budgetId, status) =>
  apiClient(`${BASE}/budgets/${budgetId}/status`, {
    method: 'POST',
    body: JSON.stringify({ status }),
  });

export const deleteBudget = async (budgetId) =>
  apiClient(`${BASE}/budgets/${budgetId}`, {
    method: 'DELETE',
  });

export const addBudgetLine = async (budgetId, payload) =>
  apiClient(`${BASE}/budgets/${budgetId}/lines`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });

export const updateBudgetLine = async (budgetId, lineId, payload) =>
  apiClient(`${BASE}/budgets/${budgetId}/lines/${lineId}`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });

export const deleteBudgetLine = async (budgetId, lineId) =>
  apiClient(`${BASE}/budgets/${budgetId}/lines/${lineId}`, {
    method: 'DELETE',
  });

export const reclassifyBudget = async (budgetId, payload) =>
  apiClient(`${BASE}/budgets/${budgetId}/reclassify`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });

export const addBudgetCostItem = async (budgetId, payload) =>
  apiClient(`${BASE}/budgets/${budgetId}/cost-items`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });

export const updateBudgetCostItem = async (budgetId, itemId, payload) =>
  apiClient(`${BASE}/budgets/${budgetId}/cost-items/${itemId}`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });

export const deleteBudgetCostItem = async (budgetId, itemId) =>
  apiClient(`${BASE}/budgets/${budgetId}/cost-items/${itemId}`, {
    method: 'DELETE',
  });

export const getBudgetCatalog = async () => apiClient(`${BASE}/budget-catalog`);

export const addBudgetSalesItem = async (budgetId, payload) =>
  apiClient(`${BASE}/budgets/${budgetId}/sales-items`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });

export const updateBudgetSalesItem = async (budgetId, itemId, payload) =>
  apiClient(`${BASE}/budgets/${budgetId}/sales-items/${itemId}`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });

export const deleteBudgetSalesItem = async (budgetId, itemId) =>
  apiClient(`${BASE}/budgets/${budgetId}/sales-items/${itemId}`, {
    method: 'DELETE',
  });

export const addBudgetProductCost = async (budgetId, payload) =>
  apiClient(`${BASE}/budgets/${budgetId}/product-costs`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });

export const updateBudgetProductCost = async (budgetId, itemId, payload) =>
  apiClient(`${BASE}/budgets/${budgetId}/product-costs/${itemId}`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });

export const deleteBudgetProductCost = async (budgetId, itemId) =>
  apiClient(`${BASE}/budgets/${budgetId}/product-costs/${itemId}`, {
    method: 'DELETE',
  });

export const addBudgetVariableItem = async (budgetId, payload) =>
  apiClient(`${BASE}/budgets/${budgetId}/variable-items`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });

export const updateBudgetVariableItem = async (budgetId, itemId, payload) =>
  apiClient(`${BASE}/budgets/${budgetId}/variable-items/${itemId}`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });

export const deleteBudgetVariableItem = async (budgetId, itemId) =>
  apiClient(`${BASE}/budgets/${budgetId}/variable-items/${itemId}`, {
    method: 'DELETE',
  });

export const getBudgetAccounts = async () => apiClient(`${BASE}/budget-accounts`);

export const createBudgetAccount = async (payload) =>
  apiClient(`${BASE}/budget-accounts`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });

export const updateBudgetAccount = async (accountId, payload) =>
  apiClient(`${BASE}/budget-accounts/${accountId}`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });

export const deleteBudgetAccount = async (accountId) =>
  apiClient(`${BASE}/budget-accounts/${accountId}`, {
    method: 'DELETE',
  });
